import React, {useState, useCallback, useContext, useEffect} from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import DataState from 'app/src/components/DataState';
import Input from 'app/src/elements/inputs';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { View, Text, Pressable } from 'react-native';
import SubNav from './SubNav';
import { useNavigate } from '../../utils/routing';
import { CREATE_ASSET, EXISTING_ASSETS, SELECTS, LOCATION_SELECTS } from './queries';
import { ButtonContainer } from './elements';
import { useParams } from 'react-router-dom';
import { AssetsContainer } from './elements';
import moment from 'moment';
import AssignToUsers from '../Assets/AssignToUsers';
import AuthContext from '../../contexts/AuthContext';
import elRed from '../../styles/colors';
import {NewAssetDocuments} from '../Attachments/new';
import CustomLocation from '../Locations/CustomLocation';
import Autocomplete from '../Autocomplete';

const NewAsset = ({setShowModal, refetch, withNav}) => {
  const { sellerId, clientNumberLabelFull, sellerName } = useContext(AuthContext);
  const [submitting, setSubmitting] = useState();
  const [selectedSellerUsers, setSelectedSellerUsers] = useState([]);
  const [existingVins, setExistingVins] = useState([]);
  const [showDuplicateVin, setShowDuplicateVin] = useState(false);
  const [linkedAttachments, setLinkedAttachments] = useState([]);
  const [showCustomLocationField, setShowCustomLocationField] = useState();
  const [sellerDocuments, setSellerDocuments] = useState([]);
  const [locations, setLocations] = useState();
  const navigate = useNavigate();
  const [createAsset] = useMutation(CREATE_ASSET, {
    onCompleted: (response) => {
      if(refetch) {
        setShowModal(false);
        refetch();
      }
      navigate(`/assets/${response.createAsset.asset.id}`)
    }
  });

  const [ loadSelectOptions, { loading: loading, data: nearbyData } ] =
    useLazyQuery(SELECTS, {
      onCompleted: (response) => {

      }
    }
  );

  const [ loadLocationOptions, { loading: locationsLoading, data: locationsData } ] =
    useLazyQuery(LOCATION_SELECTS, {
      onCompleted: (response) => {
      }
    }
  );

  const onSubmit = useCallback(input => {
    setSubmitting(true);
    input['year'] = parseInt(input['year']);
    if(input['linkedAttachments']) {
      input['linkedAttachmentAttributes'] = linkedAttachments;
      delete input['linkedAttachments'];
    }
    input['sellerUserIds'] = selectedSellerUsers;
    if(input['sellerDocuments']) {
      input['sellerDocumentNotes'] = input['sellerDocuments'].map(document => {
        return document.notes || ''
      });
      input['sellerDocuments'] = input['sellerDocuments'].map(document => {
        return document
      })
    }
    input['customLocation'] = true;
    if(input['uccExpiresOn'] === "") {
      input['uccExpiresOn'] = null;
    }
    createAsset({ variables: input });
  });

  const { control, watch, handleSubmit, register, setValue, resetField,
    formState: { errors }
  } = useForm({
    defaultValues: {
      sellerId: sellerId,
      year: parseInt(moment().format('YYYY')),
      make: '',
      model: '',
      vin: '',
      clientNumber: '',
      sellerNotes: '',
      sellerDocuments: [],
      consignmentMethod: "Dropoff",
      drivable: true,
      locationId: '',
      consignmentLocationId: '',
      customConsignmentAddress: '',
      street: '',
      city: '',
      state: '',
      postalCode: '',
      uccExpiresOn: ''
    }
  });

  const { data } = useQuery(EXISTING_ASSETS);

  useEffect(() => {
    if(data) {
      const vins = data.assets
        .filter(asset => !asset.removedAt)
        .map(asset => asset.vin);

      setExistingVins(vins);
    }
  }, [data])

  const watchVin = watch('vin');
  const watchConsignmentMethod = watch('consignmentMethod');
  const watchDrivable = watch('drivable');
  const watchStreet = watch('street');
  const watchCity = watch('city');
  const watchState = watch('state');
  const watchZip = watch('postalCode');

  useEffect(() => {
    if(watchCity || watchZip) {
      loadSelectOptions({
        variables: {
          address: [watchStreet, watchCity, watchState, watchZip].filter(Boolean).join(", ") ||
          null
        }
      });
    }
  }, [watchCity, watchZip])

  useEffect(() => {
    if(!locationsData) {
      loadLocationOptions();
    }
    if(locationsData) {
      setLocations(locationsData?.locations)
    }
  }, [locationsData])

  const checkVin = () => {
    if(existingVins && watchVin) {
      return existingVins.includes(watchVin);
    }
  }

  useEffect(() => {
    if(watchVin) {
      let exists = checkVin();
      if(exists) {
        setShowDuplicateVin(true);
      }else {
        setShowDuplicateVin(false);
      }
    }else {
      setShowDuplicateVin(false);
    }
  }, [watchVin])

  return (
    <View>
      {withNav &&
        <SubNav sellerId={sellerId} sellerName={sellerName} />
      }
      <AssetsContainer>
        {submitting ?
          DataState.Loading :
          <View>
            <Input.YearPicker
              label="Year:"
              name="year"
              control={control}
              errors={errors}
              {...register("year", {
                required: "Required",
              })}
            />
            {/* <Input.Text
              label="Make:"
              name="make"
              control={control}
              errors={errors}
              {...register("make", {
                required: "Required",
              })}
            /> */}
            <Autocomplete
              label={'Make'}
              placeHolder={''}
              resource={'InputOption'}
              setValue={setValue}
              name='make'
              zIndex={2}
              {...register('make', { required: 'Required' })}
            />
            {/* <Input.Text
              label="Model:"
              name="model"
              control={control}
              errors={errors}
              {...register("model", {
                required: "Required",
              })}
            /> */}
            <Autocomplete
              label={'Model'}
              placeHolder={''}
              resource={'InputOption'}
              setValue={setValue}
              name='model'
              zIndex={1}
              {...register('model', { required: 'Required' })}
            />
            <Input.Text
              label="VIN:"
              name="vin"
              control={control}
              errors={errors}
              {...register('vin', {
                maxLength: { value: 17, message: 'Max 17 characters' }
              })}
            />
            {showDuplicateVin &&
              <View style={{marginTop: '-10px', marginBottom: '10px'}}>
                <Text style={{fontWeight: 'bold', color: elRed}}>
                  Asset already exists within system. If you need this VIN added,
                  please contact Equiplinc Auction Group.
                </Text>
              </View>
            }
            <Input.Text
              label={`${clientNumberLabelFull}:`}
              name="clientNumber"
              control={control}
            />
            <Input.Text
              label="Seller Notes:"
              name="sellerNotes"
              control={control}
              errors={errors}
            />
            <CustomLocation
              control={control}
              errors={errors}
            />
            <br />
            <Input.Picker
              label="Is the Asset to be dropped off or does EquipLinc need to pick it up?"
              name="consignmentMethod"
              control={control}
              errors={errors}
              options={
                ["Dropoff", "Pickup"].map(option => {
                  return {value: option, label: option}
                })
              }
            />
            <Input.Checkbox
              label="Is Asset Drivable?"
              name="drivable"
              checked={watchDrivable}
              control={control}
              errors={errors}
            />
            {!showCustomLocationField && (nearbyData || locations)  &&
              <Input.Picker
                label={
                  watchConsignmentMethod === 'Dropoff' ?
                    'Dropoff Location' :
                    'Pickup Location'
                }
                name="consignmentLocationId"
                control={control}
                errors={errors}
                options={getLocationOptions(true)}
              />
            }
            {showCustomLocationField &&
              <Input.Text
                label='Custom Pickup Location'
                name="customConsigmentAddress"
                control={control}
              />
            }
            {watchConsignmentMethod === 'Pickup' &&
              <View>
                {!showCustomLocationField ?
                  <Pressable
                    onPress={() => {
                      setValue('consignmentLocationId', null);
                      setShowCustomLocationField(true)
                    }}
                  >
                    <Text
                      style={{
                        fontSize: '14px',
                        marginTop: '-5px',
                        marginLeft: '10px'
                      }}
                    >
                      Custom Pickup Location
                    </Text>
                  </Pressable> :
                  <Pressable
                  onPress={() => {
                    setValue('customConsignmentAddress', null);
                    setShowCustomLocationField(false)
                  }}>
                    <Text
                      style={{
                        fontSize: '14px',
                        marginTop: '-5px',
                        marginLeft: '10px'
                      }}
                    >
                      Select Existing Location
                    </Text>
                  </Pressable>
                }
              </View>
            }
            <br />
            <Text
              style={{marginBottom: '-15px', fontSize: '16px'}}>
              Files:
            </Text>
            {/* <br />
              {expense.expenseFiles && expense.expenseFiles.map(file =>
                <View style={{width: 'fit-content'}}>
                  <Link to={file.url} target='_blank'>
                    <Text>
                      {file.filename}
                    </Text>
                  </Link>
                </View>
              )} */}
            <View>
              <NewAssetDocuments
                recordId={null}
                recordType='Asset'
                attachmentType='Seller Document'
                refetch={refetch}
                name='sellerDocuments'
                setValue={setValue}
                existingFiles={[]}
              />
            </View>
            {/* {watchDocuments.length > 0 &&
              <Text style={{fontWeight: 'bold'}}>Added Documents:</Text>
            }
            {watchDocuments?.length > 0 &&
              watchDocuments?.map((file, index) => {
                return (
                  <View
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      marginBottom: '20px'
                    }}
                  >
                    <Text
                      style={{marginRight: '20px'}}
                    >
                      {`${file.name} ${!file.id ? '(Not Saved)' : null}`}
                    </Text>
                    <Text
                      style={{marginRight: '10px'}}
                    >
                      File notes:
                    </Text>
                    <input
                      type='text'
                      style={{flex: 1}}
                      onChange={(e) => setFileNotes(index, e.target.value)}
                    />
                  </View>
                )
              })
            } */}
            <Input.Date
              label="Eligible Sale Date:"
              name="uccExpiresOn"
              control={control}
              errors={errors}
              {...register("uccExpiresOn", {
                valueAsDate: true,
              })}
            />
            <View
              style={{maxWidth: '60vw'}}
            >
              <AssignToUsers
                selectedSellerUsers={selectedSellerUsers}
                setSelectedSellerUsers={setSelectedSellerUsers}
              />
            </View>
            <ButtonContainer>
              <ButtonGroup>
                <Button
                  label="Create Asset"
                  onPress={handleSubmit(onSubmit)}
                  disabled={showDuplicateVin}
                  style={showDuplicateVin ?
                    {opacity: 0.5} :
                    {}
                  }
                />
              </ButtonGroup>
            </ButtonContainer>
          </View>
        }
      </AssetsContainer>
    </View>
  )

  function getLocationOptions(forNearbys) {
    let locs = forNearbys && nearbyData ?
      nearbyData?.nearbyLocations[0].locations :
      locations
    locs = locs?.map(location => {
      return {
        value: location.id,
        label: [
          location.name,
          location.address,
          getLocationDistance(
            location.id,
            nearbyData ? nearbyData.nearbyLocations[0].distances : []
          )
        ].filter(Boolean).join(' - ')
      }
    })
    locs?.unshift({value: null, label: 'Select Location'});

    return locs;
  }

  function getLocationDistance(locationId, distances) {
    let distance = null;
    distances?.map(d => {
      if(parseInt(d.id) === parseInt(locationId)) {
        distance = d.distance;
      }
    })
    return distance ? `${parseFloat(distance).toFixed(0)} miles` : null;
  }
}

export default NewAsset;
